import React from 'react';
import { Input } from 'reactstrap';
import {
    TermForm,
    GeneralDeferredPaymentsInformation,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { TermFormFieldComment } from '../../../../../../slices/marketplace/loi-form/term-form-field-comment';
import { LoiTermFormFieldKeys } from '../../../../../../slices/marketplace/loi-form/loi-term-form-field-keys';
import InfoTooltip from '../../../../../../components/info-tooltip/info-tooltip';
import './TermsFormGeneralDeferredPaymentsBlock.scss';

const SECTION_TOOLTIP =
    'Obligor Name is required. Complete all financial fields, or add a comment explaining why they are not provided, to submit.';
const SENIOR_DEBT_TOOLTIP = 'All debt that is senior to deferred payment obligations of the obligor';
const ALL_OTHER_LIABILITIES_TOOLTIP = 'All non-senior actual and contingent liabilities';

export interface TermsFormGeneralDeferredPaymentsBlockProps {
    termForm: TermForm;
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    fieldComments?: TermFormFieldComment[];
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
    onAdminCommentChange?: (fieldKey: string, adminComment: string | null) => void;
    isAdmin?: boolean;
}

export const TermsFormGeneralDeferredPaymentsBlock: React.FC<
    TermsFormGeneralDeferredPaymentsBlockProps
> = ({
    termForm,
    readOnly = false,
    onChange,
    errors,
    isBuyerReview,
    isBuyerReviewConfirmed,
    fieldComments,
    onFieldCommentChange,
    onAdminCommentChange,
    isAdmin,
}) => {
    const info: GeneralDeferredPaymentsInformation | null | undefined =
        termForm.generalDeferredPaymentsInformation;

    const isBuyerEditingReview = !!isBuyerReview && !isBuyerReviewConfirmed;
    const effectiveReadOnly = isBuyerEditingReview ? false : readOnly;

    const update = (patch: Partial<GeneralDeferredPaymentsInformation>) => {
        onChange?.({
            ...termForm,
            generalDeferredPaymentsInformation: {
                ...(info ?? {}),
                ...patch,
            },
        });
    };

    const getComment = (fieldKey: string) =>
        fieldComments?.find((c) => c.fieldKey === fieldKey)?.buyerComment ?? null;
    const getAdminComment = (fieldKey: string) =>
        fieldComments?.find((c) => c.fieldKey === fieldKey)?.adminComment ?? null;

    const showComments = isBuyerReview || !!isBuyerReviewConfirmed;
    const isAdminCommentEditable = !!isAdmin && !readOnly;
    const showAdminComment = showComments || isAdminCommentEditable;

    const commentInput = (fieldKey: string) => {
        const comment = getComment(fieldKey);
        return (
            <Input
                type="textarea"
                value={comment ?? ''}
                readOnly={!isBuyerReview}
                onChange={(e) =>
                    onFieldCommentChange?.(fieldKey, e.target.value === '' ? null : e.target.value)
                }
                className="terms-form-comment-input"
                aria-label={`Comment for ${fieldKey}`}
            />
        );
    };

    const adminCommentInput = (fieldKey: string) => {
        const adminComment = getAdminComment(fieldKey);
        return (
            <Input
                type="textarea"
                value={adminComment ?? ''}
                readOnly={!isAdminCommentEditable}
                onChange={(e) =>
                    onAdminCommentChange?.(fieldKey, e.target.value === '' ? null : e.target.value)
                }
                className="terms-form-comment-input"
                aria-label={`Admin comment for ${fieldKey}`}
            />
        );
    };

    return (
        <div className="terms-form-detail-block terms-form-section-underlined terms-form-general-deferred-payments-block">
            <div className="terms-form-block-name terms-form-general-deferred-payments-header">
                Deferred Payment Obligor Information
                <InfoTooltip
                    text={SECTION_TOOLTIP}
                    idText="general-deferred-payments-section"
                />
            </div>
            <div className="terms-form-price-summary-table-container">
                <table className="terms-form-price-summary-table">
                    <thead>
                        <tr>
                            <th>Obligor Name</th>
                            <th>
                                Senior Debt,{' '}
                                <span className="terms-form-header-tooltip-group">
                                    $
                                    <InfoTooltip
                                        text={SENIOR_DEBT_TOOLTIP}
                                        idText="general-deferred-payments-senior-debt"
                                    />
                                </span>
                            </th>
                            <th>
                                All Other Liabilities,{' '}
                                <span className="terms-form-header-tooltip-group">
                                    $
                                    <InfoTooltip
                                        text={ALL_OTHER_LIABILITIES_TOOLTIP}
                                        idText="general-deferred-payments-all-other-liabilities"
                                    />
                                </span>
                            </th>
                            <th>Balance Sheet Cash, $</th>
                            <th>LTM EBITDA (Pro Forma), $</th>
                            {showAdminComment && (
                                <th className="terms-form-col-comment terms-form-col-admin-comment">Admin Comment</th>
                            )}
                            {showComments && (
                                <th className="terms-form-col-comment">Buyer Comment</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <Input
                                    type="text"
                                    value={info?.obligorName ?? ''}
                                    disabled={effectiveReadOnly}
                                    onChange={(e) =>
                                        update({ obligorName: e.target.value || null })
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="Obligor Name"
                                    invalid={errors?.has(LoiTermFormFieldKeys.obligorName)}
                                />
                            </td>
                            <td>
                                <Input
                                    type="text"
                                    value={info?.seniorDebt ?? ''}
                                    disabled={effectiveReadOnly}
                                    onChange={(e) =>
                                        update({ seniorDebt: e.target.value || null })
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="Senior Debt"
                                    invalid={errors?.has(LoiTermFormFieldKeys.seniorDebt)}
                                />
                            </td>
                            <td>
                                <Input
                                    type="text"
                                    value={info?.allOtherLiabilities ?? ''}
                                    disabled={effectiveReadOnly}
                                    onChange={(e) =>
                                        update({ allOtherLiabilities: e.target.value || null })
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="All Other Liabilities"
                                    invalid={errors?.has(LoiTermFormFieldKeys.allOtherLiabilities)}
                                />
                            </td>
                            <td>
                                <Input
                                    type="text"
                                    value={info?.balanceSheetCash ?? ''}
                                    disabled={effectiveReadOnly}
                                    onChange={(e) =>
                                        update({ balanceSheetCash: e.target.value || null })
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="Balance Sheet Cash"
                                    invalid={errors?.has(LoiTermFormFieldKeys.balanceSheetCash)}
                                />
                            </td>
                            <td>
                                <Input
                                    type="text"
                                    value={info?.ltmEbitdaProForma ?? ''}
                                    disabled={effectiveReadOnly}
                                    onChange={(e) =>
                                        update({ ltmEbitdaProForma: e.target.value || null })
                                    }
                                    className="form-control loi-form-input"
                                    aria-label="LTM EBITDA (Pro Forma)"
                                    invalid={errors?.has(LoiTermFormFieldKeys.ltmEbitdaProForma)}
                                />
                            </td>
                            {showAdminComment && (
                                <td className="terms-form-col-comment terms-form-col-admin-comment">
                                    {adminCommentInput(LoiTermFormFieldKeys.generalDeferredPaymentsInformation)}
                                </td>
                            )}
                            {showComments && (
                                <td className="terms-form-col-comment">
                                    {commentInput(LoiTermFormFieldKeys.generalDeferredPaymentsInformation)}
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
